import React, { useState, useEffect, useRef } from 'react';
import { useAcademicData } from './useAcademicData';

function AcademicContextSelector({
  tenantId,
  departmentId,
  onContextChange,
  initialAcademicYearId = '',
  initialSemesterId = null,
  initialTermId = null,
  showLabels = true,
  compact = false,
  token = null,
}) {
  const {
    academicYears,
    selectedAcademicYear,
    setSelectedAcademicYear,
    semesters,
    selectedSemester,
    setSelectedSemester,
    setSelectedClassId,
    terms
  } = useAcademicData(tenantId, token, initialSemesterId);
  
  // Local state for selections
  const [selectedTerm, setSelectedTerm] = useState(initialTermId || '');
  
  // Track if we've applied initial values - use a ref to prevent re-renders
  const initialValuesApplied = useRef({
    academicYear: false,
    semester: false,
    term: false,
    didMount: false // NEW: only apply initial values on first mount
  });

  // Removed effect that synchronizes local state with initial props on every change

  // Store the actual isSemesterBased from the selected academic year
  const [actualIsSemesterBased, setActualIsSemesterBased] = useState(false);
  
  // Removed unused prevInitialProps (was causing eslint warning)

  // Update actualIsSemesterBased when selectedAcademicYear changes
  useEffect(() => {
    if (selectedAcademicYear) {
      const year = academicYears.find(y => y._id === selectedAcademicYear);
      if (year) {
        setActualIsSemesterBased(year.isSemesterBased || false);
      }
    }
  }, [selectedAcademicYear, academicYears]);

  // Only apply initial values on first mount, not on every prop change
  useEffect(() => {
    if (!initialValuesApplied.current.didMount) {
      initialValuesApplied.current.didMount = true;
      // allow initial value application logic to run
    }
  }, []);

  // Apply initial academic year ONLY on first mount
  useEffect(() => {
    if (
      initialAcademicYearId &&
      !initialValuesApplied.current.academicYear &&
      academicYears.length > 0 &&
      initialValuesApplied.current.didMount
    ) {
      const yearExists = academicYears.some(y => y._id === initialAcademicYearId);
      if (yearExists && initialAcademicYearId !== selectedAcademicYear) {
        console.log('Setting initial academic year to:', initialAcademicYearId);
        setSelectedAcademicYear(initialAcademicYearId);
      }
      initialValuesApplied.current.academicYear = true;
    }
  }, [initialAcademicYearId, academicYears, selectedAcademicYear, setSelectedAcademicYear]);

  // Apply initial semester (if any) - only if year is semester-based, only on first mount
  useEffect(() => {
    if (
      initialSemesterId &&
      !initialValuesApplied.current.semester &&
      semesters.length > 0 &&
      actualIsSemesterBased &&
      initialValuesApplied.current.didMount
    ) {
      const semesterExists = semesters.some(s => s._id === initialSemesterId);
      if (semesterExists && initialSemesterId !== selectedSemester) {
        console.log('Setting initial semester to:', initialSemesterId);
        setSelectedSemester(initialSemesterId);
      }
      initialValuesApplied.current.semester = true;
    }
  }, [initialSemesterId, semesters, selectedSemester, setSelectedSemester, actualIsSemesterBased]);

  // Apply initial term - ONLY if year is NOT semester-based, only on first mount
  useEffect(() => {
    if (
      initialTermId &&
      !initialValuesApplied.current.term &&
      terms.length > 0 &&
      !actualIsSemesterBased &&
      initialValuesApplied.current.didMount
    ) {
      const termExists = terms.some(t => t._id === initialTermId);
      if (termExists && initialTermId !== selectedTerm) {
        console.log('Setting initial term to:', initialTermId);
        setSelectedTerm(initialTermId);
      }
      initialValuesApplied.current.term = true;
    }
  }, [initialTermId, terms, selectedTerm, actualIsSemesterBased]);

  // Reset term when academic year changes or becomes semester-based
  useEffect(() => {
    if (actualIsSemesterBased || !selectedAcademicYear) {
      setSelectedTerm('');
    }
  }, [actualIsSemesterBased, selectedAcademicYear]);

  // Reset semester when academic year becomes term-based
  useEffect(() => {
    if (!actualIsSemesterBased) {
      setSelectedSemester('');
    }
  }, [actualIsSemesterBased, setSelectedSemester]);

  // Notify parent when context changes - with debounce to prevent multiple emissions
  const lastContextRef = useRef();
  const timeoutRef = useRef();
  
  useEffect(() => {
    if (!selectedAcademicYear) return;

    const selectedYear = academicYears.find(y => y._id === selectedAcademicYear) || null;
    const selectedSem = semesters.find(s => s._id === selectedSemester) || null;
    const selectedTermObj = terms.find(t => t._id === selectedTerm) || null;

    // Debug: Log fetched terms
    console.log('[AcademicContextSelector] terms:', terms);

    const newContext = {
      academicYearId: selectedAcademicYear,
      semesterId: actualIsSemesterBased ? selectedSemester : null,
      termId: !actualIsSemesterBased ? selectedTerm : null,
      academicYear: selectedYear,
      semester: selectedSem,
      term: selectedTermObj,
      isSemesterBased: actualIsSemesterBased,
      terms: terms // Pass terms array for rendering
    };

    // Debug: Log context emission
    console.log('[AcademicContextSelector] Emitting context change:', newContext);

    // Skip if no change
    if (JSON.stringify(lastContextRef.current) === JSON.stringify(newContext)) {
      return;
    }

    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Debounce the emission
    timeoutRef.current = setTimeout(() => {
      lastContextRef.current = newContext;
      onContextChange?.(newContext);
    }, 100);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [selectedAcademicYear, selectedSemester, selectedTerm, actualIsSemesterBased, academicYears, semesters, terms, onContextChange]);

  // Handle term change
  const handleTermChange = (termId) => {
    console.log('Term changed to:', termId);
    setSelectedTerm(termId);
    setSelectedClassId('');
    
    // Mark term as applied since user manually changed it
    initialValuesApplied.current.term = true;
  };

  // Handle academic year change
  const handleAcademicYearChange = (academicYearId) => {
    console.log('Academic year changed to:', academicYearId);
    setSelectedAcademicYear(academicYearId);
    setSelectedSemester('');
    setSelectedTerm('');
    setSelectedClassId('');
    
    // Reset flags when user manually changes
    initialValuesApplied.current = {
      academicYear: true,
      semester: false,
      term: false
    };
  };

  // Handle semester change
  const handleSemesterChange = (semesterId) => {
    console.log('Semester changed to:', semesterId);
    setSelectedSemester(semesterId);
    setSelectedClassId('');
    
    // Mark semester as applied since user manually changed it
    initialValuesApplied.current.semester = true;
  };

  // Determine if we should show term selector
  const showTermSelector = !actualIsSemesterBased && terms.length > 0 && selectedAcademicYear;

  return (
    <div className={`space-y-${compact ? '2' : '4'}`}>
      {showLabels && (
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Academic Context</h3>
          <p className="text-xs text-gray-600 mt-1">
            Select the academic context for this subject
          </p>
        </div>
      )}
      
      <div className={`grid ${compact ? 'gap-2' : 'gap-4'} ${actualIsSemesterBased ? 'grid-cols-1 md:grid-cols-2' : (showTermSelector ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1')}`}>
        {/* Academic Year Selector */}
        <div>
          {!compact && (
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Academic Year *
            </label>
          )}
          <select
            value={selectedAcademicYear || ''}
            onChange={(e) => handleAcademicYearChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            required
          >
            <option value="">Select Academic Year</option>
            {academicYears.map((year) => (
              <option key={year._id} value={year._id}>
                {year.name}{year.label ? ` (${year.label})` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Semester Selector - Only show for semester-based years */}
        {actualIsSemesterBased && (
          <div>
            {!compact && (
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Semester *
              </label>
            )}
            <select
              value={selectedSemester || ''}
              onChange={(e) => handleSemesterChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              required
            >
              <option value="">Select Semester</option>
              {semesters.map((semester) => (
                <option key={semester._id} value={semester._id}>
                  {semester.semesterName || `Semester ${semester.semesterNumber}`}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Term Selector - Only show for term-based years */}
        {showTermSelector && (
          <div>
            {!compact && (
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Term *
              </label>
            )}
            <select
              value={selectedTerm || ''}
              onChange={(e) => handleTermChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
              required
            >
              <option value="">Select Term</option>
              {terms.map((term) => (
                <option key={term._id} value={term._id}>
                  {term.termLabel || term.termName || `Term ${term.termNumber}`}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Context Summary */}
      {selectedAcademicYear && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center gap-2 text-blue-700 text-sm">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>
              Selected context:{' '}
              <strong>
                {(() => {
                  const ay = academicYears.find(y => y._id === selectedAcademicYear);
                  return ay ? `${ay.name}${ay.label ? ` (${ay.label})` : ''}` : '';
                })()}
                {actualIsSemesterBased && selectedSemester && (
                  ` - ${semesters.find(s => s._id === selectedSemester)?.semesterName || 'Semester'}`
                )}
                {!actualIsSemesterBased && selectedTerm && (
                  ` - ${terms.find(t => t._id === selectedTerm)?.termLabel || terms.find(t => t._id === selectedTerm)?.termName || 'Term'}`
                )}
              </strong>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default AcademicContextSelector;